import { Box, TextField, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useCreateMailpiecesListCommentMutation, useUpdatePiecesMutation } from '@api/piece';
import { useGetUsersFilteredQuery } from '@api/user';
import { EmptyResultResponse, PieceAction } from '@apiContract';
import { ActionComponentProps } from '@components/MailpieceActions';
import { useUserPermissions } from '@hooks/useUserPermissions';
import { Roles } from '@rdTypes/shared';
import { DialogConfirmation } from '@uiKit/DialogConfirmation';
import { AxiosResponse } from 'axios';
import NotifyMailroomSwitch from 'src/components/NotifyMailroomSwitch';

import { getItemsCount } from '../../utils';
import { CollapsibleOptions } from './CollapsibleOptions';

const FinishPickUpWithIDCard: FC<ActionComponentProps> = ({
  mailpieces,
  actionStarted,
  onActionFinish,
  pendingSearchValue,
}) => {
  const { isAllowed } = useUserPermissions();
  const isAdmin = isAllowed([Roles.Admin]);
  const [updatePieces] = useUpdatePiecesMutation();
  const [createMailpiecesListComment] = useCreateMailpiecesListCommentMutation();
  const [actionInProgress, setActionInProgress] = useState(false);
  const [idCard, setIdCard] = useState('');
  const [idCardError, setIdCardError] = useState<string | null>(null);
  const [skipNotifyRecipient, setSkipNotifyRecipient] = useState(false);
  const [comment, setComment] = useState('');
  const [notifyMailroom, setNotifyMailroom] = useState(true);

  const recipientId = mailpieces[0]?.recipient?.user?.id;
  const count = getItemsCount(mailpieces);

  const { data: usersData } = useGetUsersFilteredQuery(
    { Ids: recipientId ? [recipientId] : [] },
    { skip: !recipientId || !actionStarted },
  );

  const recipientFull = usersData?.items?.[0];

  const isSearchMatching = useMemo(() => {
    const search = pendingSearchValue?.trim();
    if (!search) return false;

    const recipientUserName = mailpieces[0]?.recipient?.user?.userName;
    const isRecipientMatch = recipientUserName === search;

    const assistants = recipientFull?.assistants || [];
    const isAssistantMatch = assistants.some((assistant) => assistant.userName === search);

    return isRecipientMatch || isAssistantMatch;
  }, [pendingSearchValue, mailpieces, recipientFull]);

  const initialIdValue = isSearchMatching && pendingSearchValue ? pendingSearchValue : '';

  const handleClose = useCallback(() => {
    setIdCard('');
    setIdCardError(null);
    setSkipNotifyRecipient(false);
    setComment('');
    setNotifyMailroom(true);
    onActionFinish();
  }, [onActionFinish]);

  const handleSubmit = useCallback(async () => {
    const trimmedId = idCard.trim();
    if (!trimmedId) {
      setIdCardError('ID card is required');
      return;
    }

    setActionInProgress(true);

    try {
      await updatePieces({
        pieces: mailpieces.map((piece) => ({ id: piece.id })),
        action: PieceAction.FinishPickupWithIdCard,
        pickUpIdCard: trimmedId,
        shouldNotifyRecipient: !skipNotifyRecipient,
      }).unwrap();

      if (comment.trim().length) {
        await createMailpiecesListComment({
          text: comment.trim(),
          notifyMailroom,
          pieceIds: mailpieces.map((piece) => piece.id),
        }).unwrap();
      }

      handleClose();
    } catch (error) {
      const errorMessage = (error as AxiosResponse<EmptyResultResponse>)?.data?.userMessage || 'Invalid ID card value';
      setIdCardError(errorMessage);
    } finally {
      setActionInProgress(false);
    }
  }, [
    idCard,
    mailpieces,
    skipNotifyRecipient,
    comment,
    notifyMailroom,
    updatePieces,
    createMailpiecesListComment,
    handleClose,
  ]);

  useEffect(() => {
    if (actionStarted) {
      setIdCard(initialIdValue);
      setIdCardError(null);
    }
  }, [actionStarted, initialIdValue]);

  const content = (
    <Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {count > 1
          ? `Please ask the recipient to scan their ID card to complete pickup for ${count} items.`
          : 'Please ask the recipient to scan their ID card to complete pickup.'}
      </Typography>

      <TextField
        fullWidth
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        label="ID Card Value"
        value={idCard}
        disabled={actionInProgress}
        error={!!idCardError}
        helperText={idCardError}
        onChange={(e) => {
          setIdCard(e.target.value);
          if (idCardError) setIdCardError(null);
        }}
      />

      <CollapsibleOptions>
        <TextField
          sx={{ width: '100%', mt: 1, mb: 2 }}
          label="Optional Comment"
          multiline
          rows={3}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
        <NotifyMailroomSwitch
          checked={notifyMailroom}
          onChange={() => setNotifyMailroom(!notifyMailroom)}
          label="Notify mailroom users about comment"
        />
        {isAdmin && (
          <Box sx={{ mt: 1 }}>
            <NotifyMailroomSwitch
              checked={skipNotifyRecipient}
              onChange={() => setSkipNotifyRecipient(!skipNotifyRecipient)}
              label="Skip notification to recipient about completed request"
            />
          </Box>
        )}
      </CollapsibleOptions>
    </Box>
  );

  return (
    <DialogConfirmation
      open={actionStarted}
      loading={actionInProgress}
      maxWidth="sm"
      title={count > 1 ? 'Complete pickup for selected items with ID Card' : 'Complete pickup with ID Card'}
      content={content}
      acceptButtonHandleClick={handleSubmit}
      cancelButtonHandleClick={handleClose}
      acceptButtonTitle="Complete"
      cancelButtonTitle="Cancel"
      acceptButtonProps={{ disabled: !idCard.trim() }}
    />
  );
};

export default FinishPickUpWithIDCard;
